import React from 'react';
import { Assumptions } from '../types/compensation';
import { computeModel, defaultAssumptions } from '../utils/compensation';
import { presets } from '../data/presets';
import { ThemeTriad } from '../components/ThemeTriad';
import { PresetBar } from '../components/PresetBar';
import { LeverPanel } from '../components/LeverPanel';
import { SummaryCard } from '../components/SummaryCard';
import { CommissionPanel } from '../components/CommissionPanel';
import { EquityPanel } from '../components/EquityPanel';
import { ScenarioPanel } from '../components/ScenarioPanel';
import { AssumptionsPanel } from '../components/AssumptionsPanel';
import { ProposalPanel } from '../components/ProposalPanel';
interface ConfiguratorProps {
  initialPreset: string;
}
export function Configurator({
  initialPreset
}: ConfiguratorProps) {
  const start = presets.find(p => p.id === initialPreset) ?? presets.find(p => p.recommended) ?? presets[0];
  const [assumptions, setAssumptions] = React.useState<Assumptions>(defaultAssumptions);
  const [fixedMonthly, setFixedMonthly] = React.useState(start.fixedMonthly);
  const [commissionShare, setCommissionShare] = React.useState(start.commissionShare);
  const model = computeModel(fixedMonthly, commissionShare, assumptions);
  const activePreset = presets.find(p => p.fixedMonthly === model.fixedMonthly && Math.abs(p.commissionShare - model.commissionShare) < 0.005)?.id ?? null;
  const applyPreset = (id: string) => {
    const preset = presets.find(p => p.id === id);
    if (!preset) return;
    setFixedMonthly(preset.fixedMonthly);
    setCommissionShare(preset.commissionShare);
  };
  const patchAssumptions = (patch: Partial<Assumptions>) => setAssumptions(prev => ({
    ...prev,
    ...patch
  }));
  return <main className="min-h-screen w-full bg-ink-950 text-paper" data-id="element-228" data-mp="1on9or238pd8u">
      <header className="border-b border-line" data-id="element-229" data-mp="1kmg2se9cy8il">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-6 px-5 py-5 lg:px-10" data-id="element-230" data-mp="1klo3otgjxsnk">
          <div className="flex items-baseline gap-3" data-id="element-231" data-mp="hb0jkl7is3z3">
            <span className="text-base font-semibold tracking-tight text-paper" data-id="element-232" data-mp="mad95dprzp5d">
              Snakker<span className="text-paper-dim" data-id="element-233" data-mp="r9q12cvi7met">.io</span>
            </span>
            <span className="hidden text-sm text-paper-dim sm:inline" data-id="element-234" data-mp="r7lol6jjhf2x">
              Compensation configurator
            </span>
          </div>
          <span className="text-xs uppercase tracking-[0.16em] text-paper-dim" data-id="element-235" data-mp="3ur7xzp9qn90g">
            Senior Commercial Lead · Pharma
          </span>
        </div>
      </header>

      <div className="mx-auto max-w-[1240px] px-5 pb-24 pt-12 lg:px-10 lg:pt-16" data-id="element-236" data-mp="7lqs97ki2pc1">
        <div className="max-w-3xl" data-id="element-237" data-mp="7jmfs18jci05">
          <h1 className="text-[38px] font-semibold leading-[1.05] tracking-tightest text-paper lg:text-[54px]" data-id="element-238" data-mp="ccz224pqscpq">
            Design your compensation mix
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-paper-muted" data-id="element-239" data-mp="3by4datei06hz">
            Choose the balance between security, performance and ownership.
            Everything comes from one pool — every krone you move to one lever
            leaves the other two.
          </p>
        </div>

        <div className="mt-12" data-id="element-240" data-mp="33gtyhxnucfng">
          <ThemeTriad model={model} data-id="element-241" data-mp="2zh31vdbmv5b0" />
        </div>

        <div className="mt-10" data-id="element-242" data-mp="2zfs0fk5nu9n2">
          <PresetBar activeId={activePreset} onSelect={applyPreset} data-id="element-243" data-mp="2ui8lsj51vuul" />
        </div>

        <div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-12" data-id="element-244" data-mp="2ukcpazz1eruc">
          <div className="lg:col-span-7" data-id="element-245" data-mp="2pkggmnfeac8g">
            <LeverPanel model={model} assumptions={assumptions} onFixedChange={setFixedMonthly} onShareChange={setCommissionShare} data-id="element-246" data-mp="2ksp075pydmw1" />
          </div>
          <div className="lg:sticky lg:top-6 lg:col-span-5" data-id="element-247" data-mp="2bsjt6woo8qie">
            <SummaryCard model={model} data-id="element-248" data-mp="2bpxmt0dioh1r" />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-2" data-id="element-249" data-mp="26s4jcf3lm156">
          <CommissionPanel model={model} assumptions={assumptions} data-id="element-250" data-mp="21uvxa3fbxcyn" />
          <EquityPanel model={model} assumptions={assumptions} onTogglePerformanceWarrants={v => patchAssumptions({
          performanceWarrants: v
        })} data-id="element-251" data-mp="5pwkj8d1d2k0" />
        </div>

        <div className="mt-6" data-id="element-252" data-mp="3s57wglf64pje">
          <ScenarioPanel model={model} data-id="element-253" data-mp="3s3wosailqxn0" />
        </div>

        <div className="mt-6" data-id="element-254" data-mp="yocafnfzn8f">
          <AssumptionsPanel assumptions={assumptions} onChange={patchAssumptions} onReset={() => setAssumptions(defaultAssumptions)} data-id="element-255" data-mp="xvfa6eacc3h" />
        </div>

        <div className="mt-14" data-id="element-256" data-mp="3n892c2vi8il7">
          <ProposalPanel model={model} assumptions={assumptions} data-id="element-257" data-mp="39752rtboe4f9" />
        </div>
      </div>
    </main>;
}